<template>
  <div
    class="carrossel-step-aparencia"
    :class="{ 'carrossel-step-aparencia--dark': darkEnabled }"
  >
    <h3 class="step-title">Aparência</h3>
    <p class="step-subtitle">
      Configure o estilo e comportamento do {{ entityLabel }}.
    </p>

    <v-divider class="mb-5" />

    <v-row>
      <v-col cols="12" lg="7">
        <section class="config-section">
          <div class="section-header">
            <div class="section-header__text">
              <h4 class="section-label">Dispositivos</h4>
              <p class="section-desc">
                Escolha em quais dispositivos o carrossel será exibido e quantos
                banners aparecem por vez.
              </p>
            </div>
            <v-icon class="section-header__icon">fas fa-tablet-alt</v-icon>
          </div>

          <div class="device-cards">
            <div class="device-card">
              <div class="device-card__top">
                <div class="device-card__identity">
                  <v-icon class="device-card__icon">fas fa-desktop</v-icon>
                  <span class="device-card__name">Desktop</span>
                </div>
                <FormSwitch
                  v-model="appearance.selectedDevice.desktop"
                  class="device-card__switch"
                  @input="onDirty"
                />
              </div>
              <template v-if="appearance.selectedDevice.desktop">
                <label class="field-label"
                  >Banners visíveis simultaneamente</label
                >
                <FormField
                  v-model="appearance.slidesToShowResponsive.desktop"
                  typeField="number"
                  minField="1"
                  step="1"
                  placeholder="Informe a quantidade"
                  :clearable="false"
                  @input="onDirty"
                />
                <small class="field-hint">
                  Quantidade de banners exibidos ao mesmo tempo em telas
                  grandes.
                </small>
              </template>
            </div>

            <div class="device-card">
              <div class="device-card__top">
                <div class="device-card__identity">
                  <v-icon class="device-card__icon">fas fa-mobile-alt</v-icon>
                  <span class="device-card__name">Mobile</span>
                </div>
                <FormSwitch
                  v-model="appearance.selectedDevice.mobile"
                  class="device-card__switch"
                  @input="onDirty"
                />
              </div>
              <template v-if="appearance.selectedDevice.mobile">
                <label class="field-label"
                  >Banners visíveis simultaneamente</label
                >
                <FormField
                  v-model="appearance.slidesToShowResponsive.mobile"
                  typeField="number"
                  minField="1"
                  step="1"
                  placeholder="Informe a quantidade"
                  :clearable="false"
                  @input="onDirty"
                />
                <small class="field-hint">
                  Quantidade de banners exibidos ao mesmo tempo em telas
                  pequenas.
                </small>
              </template>
            </div>
          </div>
        </section>

        <section class="config-section">
          <div class="section-header">
            <div class="section-header__text">
              <h4 class="section-label">Navegação</h4>
              <p class="section-desc">
                Escolha o estilo das setas e dos indicadores.
              </p>
            </div>
            <v-icon class="section-header__icon">fas fa-arrows-alt-h</v-icon>
          </div>
          <div class="nav-cards">
            <div
              v-for="preset in navigationPresets"
              :key="preset.id"
              class="nav-card"
              :class="{
                'nav-card--selected': appearance.navigationPreset === preset.id
              }"
              @click="selectNavigation(preset.id)"
            >
              <div class="nav-card__preview">
                <span v-if="preset.showArrows" class="nav-card__arrow"
                  >&lsaquo;</span
                >
                <span v-if="preset.showDots" class="nav-card__dots">
                  <span /><span /><span />
                </span>
                <span v-if="preset.showArrows" class="nav-card__arrow"
                  >&rsaquo;</span
                >
              </div>
              <span class="nav-card__label">{{ preset.label }}</span>
              <span class="nav-card__desc">{{
                navPresetDescriptions[preset.id]
              }}</span>
            </div>
          </div>
        </section>

        <section class="config-section">
          <div class="section-header">
            <div class="section-header__text">
              <h4 class="section-label">Comportamento</h4>
              <p class="section-desc">
                Defina como o carrossel se comporta durante a exibição.
              </p>
            </div>
            <v-icon class="section-header__icon">fas fa-sliders-h</v-icon>
          </div>
          <v-row class="behavior-row">
            <v-col cols="12" md="4">
              <label class="field-label">Velocidade</label>
              <FormAutocomplete
                v-model="appearance.autoplaySpeed"
                :options="velocidadeOptions"
                placeholder="Selecione a velocidade"
                :disabled="!appearance.autoplayEnabled"
                :clearable="false"
                @input="onDirty"
              />
              <small class="field-hint">
                Tempo que cada banner permanece visível.
              </small>
            </v-col>
            <v-col cols="12" md="4">
              <label class="field-label">Tipo de transição</label>
              <FormAutocomplete
                v-model="appearance.fade"
                :options="transicaoOptions"
                placeholder="Selecione o tipo"
                :clearable="false"
                @input="onDirty"
              />
              <small class="field-hint">
                Efeito aplicado na troca de banners.
              </small>
            </v-col>
            <v-col cols="12" md="4">
              <FormSwitch
                label="Exibição automática"
                v-model="appearance.autoplayEnabled"
                class="behavior-switch"
                @input="onAutoplayToggle"
              />
              <small class="field-hint behavior-switch-hint">
                Avança automaticamente para o próximo banner.
              </small>
            </v-col>
          </v-row>
        </section>

        <section class="config-section">
          <div class="section-header">
            <div class="section-header__text">
              <h4 class="section-label">Cor da navegação</h4>
              <p class="section-desc">
                Cor das setas de navegação do {{ entityLabel }} na loja. Não
                altera a cor do título exibido na página.
              </p>
            </div>
            <v-icon class="section-header__icon">fas fa-arrows-alt-h</v-icon>
          </div>
          <div class="color-picker-row">
            <FormField
              v-model="fontColor"
              class="color-field"
              placeholder="#000000FF"
              @input="onDirty"
            />
            <button
              type="button"
              class="color-swatch mt-n1"
              :style="colorSwatchStyle"
              @click="showColorPicker = !showColorPicker"
            />
            <div v-if="showColorPicker" class="color-picker-popup" @click.stop>
              <v-color-picker
                v-model="fontColor"
                hide-inputs
                @input="onDirty"
              />
            </div>
          </div>
          <small class="field-hint">
            Na pré-visualização, a cor reflete apenas as setas, como na loja.
          </small>
        </section>

        <section class="config-section">
          <div class="section-header">
            <div class="section-header__text">
              <h4 class="section-label">Configurações avançadas</h4>
              <p class="section-desc">
                Opções complementares de exibição e ordenação do carrossel.
              </p>
            </div>
            <v-icon class="section-header__icon">fas fa-cog</v-icon>
          </div>

          <v-row class="mb-2">
            <v-col cols="12" md="4" class="mb-n10">
              <label class="field-label">Ordenação na página</label>
              <FormAutocomplete
                v-model="appearance.ordem"
                :options="ordemOptions"
                placeholder="Selecione a ordenação"
                :clearable="false"
                @input="onDirty"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="3" class="mb-n10">
              <FormSwitch
                label="Ativar destaque centralizado"
                v-model="appearance.centerMode"
                @input="onDirty"
              />
            </v-col>
            <v-col cols="12" md="3" class="mb-n10">
              <FormSwitch
                v-if="!productEditorMode"
                label="Fixar topo"
                v-model="wizardState.carrossel.config.fixedTop"
                help="A altura total dos banners fixos no topo é limitada a 30% da tela."
                @input="onDirty"
              />
            </v-col>
            <v-col cols="12" md="3" class="mb-n10">
              <FormSwitch
                label="Exibir título"
                v-model="appearance.showTitle"
                @input="onDirty"
              />
            </v-col>
            <v-col cols="12" md="3" class="mb-n10">
              <FormSwitch
                v-if="!productEditorMode"
                label="Ativa banner principal"
                v-model="wizardState.carrossel.config.bannerPrincipal"
                @input="onDirty"
              />
            </v-col>
            <v-col cols="12" md="3" class="mb-n10">
              <FormSwitch
                label="Exibir paginação horizontal"
                v-model="wizardState.carrossel.config.showDots"
                @input="onNavigationDotsOverride"
              />
            </v-col>
          </v-row>
        </section>
      </v-col>

      <v-col cols="12" lg="5">
        <div class="preview-panel">
          <div class="side-card preview-card">
            <div class="section-header section-header--compact">
              <div class="section-header__text">
                <h4 class="section-label">Pré-visualização</h4>
                <p class="section-desc">
                  Veja como o carrossel ficará no site.
                </p>
              </div>
              <v-icon class="section-header__icon">fas fa-eye</v-icon>
            </div>
            <CarrosselPreVisualizacaoTemplate
              embedded
              :show-title="false"
              :hide-banner-preview="hideBannerPreview"
              :appearance-state="appearance"
              :carrossel="wizardState.carrossel"
              :slides="wizardState.carrossel.slides"
              :slide-image-revisions="wizardState.slideImageRevisions"
            />
          </div>

          <div class="side-card summary-panel">
            <div class="section-header section-header--compact">
              <div class="section-header__text">
                <h4 class="section-label">Resumo das configurações</h4>
              </div>
              <v-icon class="section-header__icon">fas fa-list-ul</v-icon>
            </div>
            <table class="summary-table">
              <tbody>
                <tr v-for="row in summaryRows" :key="row.label">
                  <td class="summary-table__label">
                    <i :class="[row.icon, 'summary-table__icon']" />
                    {{ row.label }}
                  </td>
                  <td class="summary-table__value">
                    <span
                      v-if="row.swatch"
                      class="summary-swatch"
                      :style="{ backgroundColor: row.value }"
                    />
                    {{ row.value }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import FormAutocomplete from "@/components/form/FormAutocomplete";
import FormField from "@/components/form/FormField";
import FormSwitch from "@/components/form/FormSwitch";
import CarrosselPreVisualizacaoTemplate from "../template/CarrosselPreVisualizacaoTemplate.vue";
import {
  NAVIGATION_PRESETS,
  VELOCIDADE_OPTIONS,
  TRANSICAO_OPTIONS,
  getDevicesSummary,
  getNavigationPresetLabel,
  getVelocidadeLabel,
  getTransicaoLabel,
  validateStepAparencia,
  applyNavigationPreset
} from "../../utils/utilCarrossel";
import { mapGetters } from "vuex";

export default {
  name: "CarrosselStepAparencia",
  components: {
    FormAutocomplete,
    FormField,
    FormSwitch,
    CarrosselPreVisualizacaoTemplate
  },
  props: {
    wizardState: {
      type: Object,
      required: true
    },
    productEditorMode: {
      type: Boolean,
      default: false
    },
    hideBannerPreview: {
      type: Boolean,
      default: false
    },
    showComponente: {
      type: Function,
      default: () => true
    }
  },
  data() {
    return {
      navigationPresets: NAVIGATION_PRESETS,
      velocidadeOptions: VELOCIDADE_OPTIONS,
      transicaoOptions: TRANSICAO_OPTIONS,
      showColorPicker: false,
      navPresetDescriptions: {
        arrows_dots: "Setas laterais e pontos abaixo",
        arrows_only: "Somente setas laterais",
        dots_only: "Somente pontos abaixo",
        minimal: "Indicadores discretos",
        none: "Sem controles visíveis"
      }
    };
  },
  computed: {
    ...mapGetters(["darkEnabled"]),
    appearance() {
      return this.wizardState.appearanceState;
    },
    entityLabel() {
      return this.productEditorMode ? "carrossel de produtos" : "carrossel";
    },
    ordemOptions() {
      const items = [];
      for (let i = 1; i <= 30; i++) {
        items.push({ text: String(i), value: `lista-produtos:${i}` });
      }
      return items;
    },
    fontColor: {
      get() {
        return this.wizardState.carrossel?.config?.colors?.font || "#000000FF";
      },
      set(value) {
        if (!this.wizardState.carrossel.config.colors) {
          this.wizardState.carrossel.config.colors = {
            font: "#000000FF",
            background: ""
          };
        }
        this.wizardState.carrossel.config.colors.font = value;
      }
    },
    devicesSummary() {
      return getDevicesSummary(this.appearance);
    },
    navigationLabel() {
      return getNavigationPresetLabel(this.appearance.navigationPreset);
    },
    velocidadeLabel() {
      if (!this.appearance.autoplayEnabled) {
        return "Desativada";
      }
      return getVelocidadeLabel(this.appearance.autoplaySpeed);
    },
    transicaoLabel() {
      return getTransicaoLabel(this.appearance.fade);
    },
    colorSwatchStyle() {
      const color = this.fontColor || "#000000FF";
      return {
        backgroundColor: color,
        borderColor: color
      };
    },
    summaryRows() {
      return [
        {
          icon: "fas fa-tablet-alt",
          label: "Dispositivos",
          value: this.devicesSummary
        },
        {
          icon: "fas fa-arrows-alt-h",
          label: "Navegação",
          value: this.navigationLabel
        },
        {
          icon: "fas fa-clock",
          label: "Velocidade",
          value: this.velocidadeLabel
        },
        {
          icon: "fas fa-exchange-alt",
          label: "Transição",
          value: this.transicaoLabel
        },
        {
          icon: "fas fa-redo",
          label: "Exibição automática",
          value: this.appearance.autoplayEnabled ? "Ativo" : "Inativo"
        },
        {
          icon: "fas fa-arrows-alt-h",
          label: "Cor da navegação",
          value: this.fontColor,
          swatch: true
        }
      ];
    }
  },
  methods: {
    onDirty() {
      this.$emit("dirty");
    },
    selectNavigation(presetId) {
      this.appearance.navigationPreset = presetId;
      applyNavigationPreset(presetId, this.wizardState.carrossel.config);
      this.onDirty();
    },
    onNavigationDotsOverride(value) {
      if (this.appearance.navigationPreset === "arrows_dots") {
        this.appearance.navigationPreset = value
          ? "arrows_dots"
          : "arrows_only";
      } else if (this.appearance.navigationPreset === "dots_only") {
        if (!value) {
          this.appearance.navigationPreset = "none";
        }
      }
      applyNavigationPreset(
        this.appearance.navigationPreset,
        this.wizardState.carrossel.config
      );
      this.onDirty();
    },
    onAutoplayToggle(enabled) {
      if (enabled && !this.appearance.autoplaySpeed) {
        this.appearance.autoplaySpeed = 5000;
      }
      this.onDirty();
    },
    validate() {
      const result = validateStepAparencia(this.appearance);
      if (!result.valid && result.message) {
        this.$vueOnToast.pop("warning", result.message);
      }
      return result.valid;
    },
    syncToCarrossel() {
      if (!this.wizardState.carrossel.config) {
        this.wizardState.carrossel.config = {};
      }
      applyNavigationPreset(
        this.appearance.navigationPreset,
        this.wizardState.carrossel.config
      );
    }
  }
};
</script>

<style scoped>
.step-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
  text-align: left;
}

.step-subtitle {
  color: #666;
  margin-bottom: 24px;
  text-align: left;
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #333;
}

.section-desc {
  font-size: 12px;
  color: #888;
  margin: 0;
  line-height: 1.4;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.section-header--compact {
  margin-bottom: 12px;
}

.section-header__text {
  flex: 1;
  min-width: 0;
}

.section-header__icon {
  font-size: 22px;
  color: #bdbdbd;
  flex-shrink: 0;
  margin-top: 2px;
}

.config-section {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
}

.field-label {
  font-weight: 600;
  display: block;
  margin-bottom: 8px;
  margin-left: 4px;
  font-size: 13px;
}

.field-hint {
  display: block;
  color: #888;
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;
  line-height: 1.35;
  text-align: left;
  margin-top: -10px;
}

.device-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (max-width: 960px) {
  .device-cards {
    grid-template-columns: 1fr;
  }
}

.device-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 14px;
  background: #fafafa;
}

.device-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.device-card__identity {
  display: flex;
  align-items: center;
  gap: 8px;
}

.device-card__icon {
  font-size: 18px;
  color: #666;
}

.device-card__name {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.device-card__switch {
  flex-shrink: 0;
}

.device-card__switch >>> .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.behavior-row {
  margin-top: 0;
}

.behavior-switch >>> .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.behavior-switch-hint {
  margin-top: 8px;
}

.switch-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.switch-list >>> .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.nav-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.nav-card {
  border: 2px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  min-width: 110px;
  flex: 1;
  max-width: 140px;
  text-align: center;
  transition: border-color 0.2s, color 0.2s;
}

.nav-card:hover,
.nav-card--selected {
  border-color: var(--primary-color, #5870ef);
  color: var(--primary-color, #5870ef);
}

.nav-card__preview {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  margin-bottom: 8px;
}

.nav-card__arrow {
  font-size: 18px;
  font-weight: bold;
}

.nav-card__dots {
  display: flex;
  gap: 4px;
  align-items: center;
}

.nav-card__dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  display: block;
}

.nav-card__label {
  font-size: 12px;
  font-weight: 600;
  display: block;
}

.nav-card__desc {
  display: block;
  font-size: 10px;
  color: #999;
  margin-top: 4px;
  line-height: 1.3;
}

.nav-card--selected .nav-card__desc {
  color: inherit;
  opacity: 0.85;
}

.color-picker-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  max-width: 360px;
}

.color-field {
  flex: 1;
  margin-bottom: 0;
}

.color-swatch {
  width: 44px;
  height: 44px;
  margin-top: 4px;
  border: 2px solid;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.35);
}

.color-swatch:focus-visible {
  box-shadow: 0 0 0 2px var(--primary-color, #5870ef);
}

.color-picker-popup {
  position: absolute;
  top: 56px;
  left: 0;
  z-index: 10;
}

.preview-panel {
  position: sticky;
  top: 16px;
}

.side-card {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: transparent;
}

.preview-panel >>> .carrossel-preview-embedded {
  margin: 0;
}

.preview-panel >>> .preview-section-title {
  margin-top: 0;
}

.preview-panel >>> .container-carrossel--embedded,
.preview-panel >>> .preview-placeholder {
  background: transparent;
  border-radius: 0;
  padding-left: 0;
  padding-right: 0;
}

.preview-panel >>> .container-carrossel--embedded {
  padding-bottom: 40px;
  overflow: visible;
}

.preview-panel >>> .slick-dots {
  bottom: -32px;
}

.preview-card {
  padding-bottom: 40px;
  margin-bottom: 16px;
  overflow: visible;
}

.summary-panel {
  margin-top: 0;
}

.summary-table {
  width: 100%;
  border: none;
  border-collapse: collapse;
  border-spacing: 0;
}

.summary-table td {
  border: none;
  padding: 8px 0;
  vertical-align: top;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
}

.summary-table__label {
  color: #666;
  white-space: nowrap;
  padding-right: 16px;
  width: 48%;
}

.summary-table__value {
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.summary-table__icon {
  color: #bdbdbd;
  margin-right: 6px;
  font-size: 13px;
  width: 16px;
  text-align: center;
}

.summary-swatch {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid #ccc;
  display: inline-block;
  flex-shrink: 0;
}

/* Dark mode */
.carrossel-step-aparencia--dark .step-title {
  color: rgba(255, 255, 255, 0.87);
}

.carrossel-step-aparencia--dark .step-subtitle,
.carrossel-step-aparencia--dark .section-desc,
.carrossel-step-aparencia--dark .field-hint {
  color: rgba(255, 255, 255, 0.65);
}

.carrossel-step-aparencia--dark .section-label,
.carrossel-step-aparencia--dark .field-label,
.carrossel-step-aparencia--dark .device-card__name,
.carrossel-step-aparencia--dark .summary-table__value {
  color: rgba(255, 255, 255, 0.87);
}

.carrossel-step-aparencia--dark .section-header__icon,
.carrossel-step-aparencia--dark .device-card__icon,
.carrossel-step-aparencia--dark .summary-table__label,
.carrossel-step-aparencia--dark .summary-table__icon {
  color: rgba(255, 255, 255, 0.5);
}

.carrossel-step-aparencia--dark .config-section,
.carrossel-step-aparencia--dark .side-card {
  background: var(--dark-surface, #1d1d1d);
  border-color: rgba(255, 255, 255, 0.12);
}

.carrossel-step-aparencia--dark .device-card {
  background: #2a2a2a;
  border-color: rgba(255, 255, 255, 0.12);
}

.carrossel-step-aparencia--dark .nav-card {
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.87);
}

.carrossel-step-aparencia--dark .nav-card__desc {
  color: rgba(255, 255, 255, 0.5);
}

.carrossel-step-aparencia--dark .nav-card:hover,
.carrossel-step-aparencia--dark .nav-card--selected {
  border-color: var(--primary-color, #5870ef);
  color: var(--primary-color, #5870ef);
}

.carrossel-step-aparencia--dark .nav-card--selected .nav-card__desc {
  color: inherit;
}

.carrossel-step-aparencia--dark .summary-swatch {
  border-color: rgba(255, 255, 255, 0.25);
}

.carrossel-step-aparencia--dark
  >>> .carrossel-preview-embedded--dark
  .preview-placeholder {
  color: rgba(255, 255, 255, 0.65);
}

.carrossel-step-aparencia--dark
  >>> .carrossel-preview-embedded--dark
  .custom-arrow {
  background-color: #2a2a2a !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
}
</style>
